import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  parseFolderCardList,
  detectListFormatAndSource,
  executeFetchValidatedListCards,
  AVAILABLE_IMPORT_SOURCES,
  getSourceById,
  getFormatsForSource,
  ImportSourceOption,
  ImportFormatOption,
  ParsedListCard,
  ImportListResult
} from '../../utils/listImporter';
import { 
  addCardsBatchToList, 
  addCardToBinder, 
  invalidateFoldersStatsCache,
  getGameIcon,
  findGame,
  isGame,
  getGameSlug,
  getGameName,
  getGameId,
  CARD_GAMES,
  findGameByCardNexus,
  isCardNexusGameMatch
} from '../../services/supabaseService';
import { CardImage } from '../CardImage';
import { parseCardImage } from '../CardDetailModal';

export interface ImportFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: any;
  folderId: string;
  isSystem: boolean;
  games: any[];
  userId: string | number;
  onSuccess: () => void;
}

export const ImportFolderModal: React.FC<ImportFolderModalProps> = ({
  isOpen,
  onClose,
  folder,
  folderId,
  isSystem,
  games,
  userId,
  onSuccess
}) => {
  // 1. Passo 1: Seleção da Fonte (preparado para múltiplas fontes, padrão: CardNexus)
  const [selectedSourceId, setSelectedSourceId] = useState<string>('cardnexus');

  // Fonte atual selecionada
  const currentSource: ImportSourceOption = useMemo(() => {
    return getSourceById(selectedSourceId) || AVAILABLE_IMPORT_SOURCES[0];
  }, [selectedSourceId]);

  // Formatos dinâmicos da fonte atual
  const availableFormats: ImportFormatOption[] = useMemo(() => {
    return currentSource.formats || [];
  }, [currentSource]);

  // 2. Passo 2: Seleção do Formato da Fonte
  const [selectedFormatId, setSelectedFormatId] = useState<string>('list');

  // Formato atual selecionado
  const currentFormat: ImportFormatOption = useMemo(() => {
    return availableFormats.find(f => f.id === selectedFormatId) || availableFormats[0] || {
      id: 'list',
      name: 'Lista',
      icon: 'fa-list-ol',
      description: '',
      placeholder: 'Cole suas cartas aqui...',
      fileAccept: '.txt',
      sampleContent: ''
    };
  }, [availableFormats, selectedFormatId]);

  // Sincroniza o formato selecionado quando a fonte muda
  useEffect(() => {
    if (availableFormats.length > 0) {
      if (!availableFormats.some(f => f.id === selectedFormatId)) {
        setSelectedFormatId(availableFormats[0].id);
      }
    }
  }, [availableFormats, selectedFormatId]);

  // 3. Conteúdo da Importação
  const [rawText, setRawText] = useState<string>('');
  const [selectedGameSlug, setSelectedGameSlug] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'input' | 'preview'>('input');
  const [previewLayout, setPreviewLayout] = useState<'grid' | 'list'>('grid');
  const [validatedData, setValidatedData] = useState<ImportListResult | null>(null);

  const [progress, setProgress] = useState<{ current: number; total: number; cardName: string }>({
    current: 0,
    total: 0,
    cardName: ''
  });
  const [resultSummary, setResultSummary] = useState<{
    success: boolean;
    addedCount: number;
    notFound: ParsedListCard[];
    error?: string;
  } | null>(null);
  const [hasCopiedMissing, setHasCopiedMissing] = useState<boolean>(false);
  const [showAllPreview, setShowAllPreview] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastRawTextRef = useRef<string>('');

  // Lista garantida de jogos com fallback imediato para CARD_GAMES centralizado
  const availableGames = useMemo(() => {
    return (games && games.length > 0) ? games : (CARD_GAMES as any[]);
  }, [games]);

  // Determina se o jogo é fixo pela pasta ou se deve permitir escolha
  const isGameFixed = !isSystem && !!folder?.game_id;

  useEffect(() => {
    if (!isOpen) return;

    // Reset ao abrir modal
    setSelectedSourceId('cardnexus');
    setSelectedFormatId('list');
    setRawText('');
    setIsProcessing(false);
    setActiveTab('input');
    setPreviewLayout('grid');
    setValidatedData(null);
    setResultSummary(null);
    setHasCopiedMissing(false);
    setShowAllPreview(false);
    setProgress({ current: 0, total: 0, cardName: '' });

    if (isGameFixed && folder?.game_id) {
      const fixedGame = availableGames.find(g => isGame(g.id || g.slug, folder.game_id));
      setSelectedGameSlug(fixedGame?.slug || String(folder.game_id));
    } else if (availableGames.length > 0) {
      setSelectedGameSlug(prev => prev || availableGames[0]?.slug || String(availableGames[0]?.id));
    }
  }, [isOpen, isGameFixed, folder, availableGames]);

  // Informações do jogo ativo (resolvido de forma centralizada e resiliente)
  const activeGameObj = useMemo(() => {
    const gameDef = findGame(selectedGameSlug);
    if (gameDef) {
      const matched = availableGames.find(g => isGame(g.id || g.slug, gameDef.id));
      if (matched) return matched;
      return gameDef;
    }
    return availableGames[0] || CARD_GAMES[0];
  }, [availableGames, selectedGameSlug]);

  const targetGameSlug = useMemo(() => {
    return getGameSlug(activeGameObj?.slug || selectedGameSlug);
  }, [activeGameObj, selectedGameSlug]);

  // Análise em tempo real do texto para auto-sugestão de formato
  const detectionInfo = useMemo(() => {
    return detectListFormatAndSource(rawText);
  }, [rawText]);

  const isStructuredFormat = selectedFormatId === 'csv' || selectedFormatId === 'json';

  // Todas as cartas extraídas do texto bruto respeitando fonte e formato
  const allParsedCards = useMemo(() => {
    return parseFolderCardList(rawText, {
      source: selectedSourceId as any,
      format: selectedFormatId as any
    });
  }, [rawText, selectedSourceId, selectedFormatId]);

  // Jogos identificados no arquivo exportado (com mapeamento completo para availableGames/CARD_GAMES)
  const detectedGamesInFile = useMemo(() => {
    const gameMap = new Map<string, any>();
    allParsedCards.forEach(c => {
      if (!c.detectedGameSlug) return;
      const gameDef: any = findGameByCardNexus(c.detectedGameSlug) || findGame(c.detectedGameSlug);
      const key = gameDef?.slug || getGameSlug(c.detectedGameSlug);
      if (key && !gameMap.has(key)) {
        const matched = availableGames.find(g => isGame(g.id || g.slug, gameDef?.id || key));
        gameMap.set(key, matched || gameDef || { 
          id: key, 
          slug: key, 
          name: (c as any).detectedGameName || gameDef?.name || key 
        });
      }
    });
    return Array.from(gameMap.values());
  }, [allParsedCards, availableGames]);

  // Opções selecionáveis de jogo:
  // No modo CSV ou JSON, se houver jogos identificados no arquivo carregado,
  // só são exibidos como opções selecionáveis os jogos identificados no arquivo.
  // Caso contrário (modo lista, ou sem identificação, ou antes de carregar), exibe todos os jogos disponíveis.
  const selectableGames = useMemo(() => {
    if (isGameFixed && folder?.game_id) {
      return availableGames;
    }
    if (isStructuredFormat && detectedGamesInFile.length > 0) {
      return detectedGamesInFile;
    }
    return availableGames;
  }, [isGameFixed, folder?.game_id, isStructuredFormat, detectedGamesInFile, availableGames]);

  // Sincronização inteligente do jogo:
  // 1. Quando em formato estruturado (CSV/JSON), se o jogo atual não for um dos identificados, seleciona o primeiro
  // 2. Quando o texto é recém-carregado/colado, sugere o jogo detectado
  // NUNCA reseta quando o usuário troca manualmente entre as opções disponíveis!
  useEffect(() => {
    if (isGameFixed) return;

    if (isStructuredFormat && detectedGamesInFile.length > 0) {
      const isCurrentInDetected = detectedGamesInFile.some(g => 
        isGame(g.id || g.slug, selectedGameSlug) || isCardNexusGameMatch(g.slug, selectedGameSlug)
      );
      if (!isCurrentInDetected) {
        const firstGame = detectedGamesInFile[0];
        setSelectedGameSlug(firstGame.slug || String(firstGame.id));
      }
      return;
    }

    if (rawText && rawText !== lastRawTextRef.current) {
      lastRawTextRef.current = rawText;
      if (detectionInfo.detectedGame && availableGames.length > 0) {
        const gameDef = findGameByCardNexus(detectionInfo.detectedGame.slug || detectionInfo.detectedGame.name) || 
                        findGame(detectionInfo.detectedGame.slug || detectionInfo.detectedGame.name);
        if (gameDef) {
          const matched = availableGames.find(g => isGame(g.id || g.slug, gameDef.id));
          if (matched) {
            setSelectedGameSlug(matched.slug);
          }
        }
      }
    } else if (!rawText) {
      lastRawTextRef.current = '';
    }
  }, [rawText, isStructuredFormat, detectedGamesInFile, isGameFixed, detectionInfo.detectedGame, availableGames, selectedGameSlug]);

  // Cartas filtradas para o jogo ativo (obrigatório) com checagem de aliases do CardNexus
  const parsedPreview = useMemo(() => {
    if (!allParsedCards || allParsedCards.length === 0) return [];
    
    // Se o arquivo não traz indicação de jogo nas linhas (ex: formato de lista textual simples), todas pertencem ao jogo ativo
    const hasAnyDetected = allParsedCards.some(c => !!c.detectedGameSlug);
    if (!hasAnyDetected) return allParsedCards;

    // Se o arquivo possui identificação de jogo por carta (ex: CSV ou JSON do CardNexus), filtra para o jogo obrigatório selecionado
    return allParsedCards.filter(c => {
      if (!c.detectedGameSlug) return true;
      return isCardNexusGameMatch(c.detectedGameSlug, targetGameSlug) || isGame(c.detectedGameSlug, targetGameSlug);
    });
  }, [allParsedCards, targetGameSlug]);

  const otherGamesCount = useMemo(() => {
    return allParsedCards.length - parsedPreview.length;
  }, [allParsedCards.length, parsedPreview.length]);

  const totalCardsCount = useMemo(() => {
    return parsedPreview.reduce((acc, curr) => acc + curr.quantity, 0);
  }, [parsedPreview]);

  // Validação: a escolha do jogo é sempre obrigatória
  const isGameValid = isGameFixed || (!!selectedGameSlug && !!activeGameObj);

  // Limpa os dados de prévia validada quando o texto muda
  const handleTextChange = (newText: string) => {
    setRawText(newText);
    setValidatedData(null);
    setResultSummary(null);
  };

  // Ação de Colar do Clipboard com checagem imediata de formato e jogo CardNexus
  const handlePasteClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        handleTextChange(text);
        const det = detectListFormatAndSource(text);
        if (det.source === selectedSourceId && (det.format === 'json' || det.format === 'csv' || det.format === 'list')) {
          setSelectedFormatId(det.format);
        }
        if (!isGameFixed && det.detectedGame) {
          const gameDef = findGameByCardNexus(det.detectedGame.slug || det.detectedGame.name) || 
                          findGame(det.detectedGame.slug || det.detectedGame.name);
          if (gameDef) {
            setSelectedGameSlug(gameDef.slug);
          }
        }
      }
    } catch (err) {
      console.warn('Erro ao ler área de transferência:', err);
    }
  };

  // Upload de arquivo respeitando a extensão do formato ativo e checagem imediata do jogo
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content) {
        handleTextChange(content);
        const fileName = file.name.toLowerCase();
        let targetFmt: string | undefined = undefined;
        if (fileName.endsWith('.json') && availableFormats.some(f => f.id === 'json')) {
          targetFmt = 'json';
        } else if (fileName.endsWith('.csv') && availableFormats.some(f => f.id === 'csv')) {
          targetFmt = 'csv';
        } else if (fileName.endsWith('.txt') && availableFormats.some(f => f.id === 'list')) {
          targetFmt = 'list';
        }

        const det = detectListFormatAndSource(content);
        if (det.source === selectedSourceId && (det.format === 'json' || det.format === 'csv' || det.format === 'list')) {
          targetFmt = det.format;
        }
        if (targetFmt) {
          setSelectedFormatId(targetFmt);
        }

        // Checagem e pré-seleção imediata do jogo do CardNexus
        if (!isGameFixed && det.detectedGame) {
          const gameDef = findGameByCardNexus(det.detectedGame.slug || det.detectedGame.name) || 
                          findGame(det.detectedGame.slug || det.detectedGame.name);
          if (gameDef) {
            setSelectedGameSlug(gameDef.slug);
          }
        }
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // 1. Gera a Prévia Visual das Cartas consultando o Catálogo
  const handleGeneratePreview = async () => {
    if (!rawText.trim()) return;

    if (!isGameValid) {
      setResultSummary({
        success: false,
        addedCount: 0,
        notFound: [],
        error: 'A escolha do jogo é obrigatória. Selecione um jogo para continuar.'
      });
      return;
    }

    const cardsToImport = parsedPreview;

    if (cardsToImport.length === 0) {
      const otherNames = detectedGamesInFile.map(g => g.name).join(', ');
      setResultSummary({
        success: false,
        addedCount: 0,
        notFound: [],
        error: otherGamesCount > 0 
          ? `Nenhuma carta de ${activeGameObj?.name || 'jogo selecionado'} encontrada no arquivo. O arquivo contém cartas de: ${otherNames}. Selecione o jogo correspondente no topo.`
          : 'Nenhuma carta reconhecida. Verifique se o conteúdo está no formato selecionado.'
      });
      return;
    }

    setIsProcessing(true);
    setResultSummary(null);
    setProgress({ current: 0, total: cardsToImport.length, cardName: 'Consultando catálogo oficial...' });

    try {
      const fetchResult = await executeFetchValidatedListCards(
        targetGameSlug, 
        cardsToImport,
        (current, total, cardName) => {
          setProgress({ current, total, cardName });
        }
      );

      setValidatedData(fetchResult);
      setActiveTab('preview');
      setIsProcessing(false);
    } catch (err: any) {
      console.error('Erro ao gerar prévia da lista:', err);
      setIsProcessing(false);
      setResultSummary({
        success: false,
        addedCount: 0,
        notFound: [],
        error: err.message || 'Erro ao consultar cartas do catálogo para a prévia.'
      });
    }
  };

  // 2. Confirma a Importação (adiciona na pasta)
  const handleConfirmImport = async (preloadedData?: ImportListResult | null) => {
    if (!isGameValid) {
      setResultSummary({
        success: false,
        addedCount: 0,
        notFound: [],
        error: 'A escolha do jogo é obrigatória. Selecione um jogo para continuar.'
      });
      return;
    }

    let data = preloadedData || validatedData;

    // Se ainda não gerou prévia, valida agora
    if (!data || !data.importedItems || data.importedItems.length === 0) {
      if (!rawText.trim()) return;
      const cardsToImport = parsedPreview;
      if (cardsToImport.length === 0) {
        setResultSummary({
          success: false,
          addedCount: 0,
          notFound: [],
          error: `Nenhuma carta de ${activeGameObj?.name} para importar no arquivo.`
        });
        return;
      }

      setIsProcessing(true);
      setResultSummary(null);
      setProgress({ current: 0, total: cardsToImport.length, cardName: 'Validando cartas...' });

      try {
        data = await executeFetchValidatedListCards(
          targetGameSlug, 
          cardsToImport,
          (current, total, cardName) => {
            setProgress({ current, total, cardName });
          }
        );
        setValidatedData(data);
      } catch (e: any) {
        setIsProcessing(false);
        setResultSummary({
          success: false,
          addedCount: 0,
          notFound: [],
          error: e.message || 'Erro ao consultar cartas.'
        });
        return;
      }
    }

    if (!data.importedItems || data.importedItems.length === 0) {
      setIsProcessing(false);
      setResultSummary({
        success: false,
        addedCount: 0,
        notFound: data.notFoundItems || [],
        error: `Nenhuma carta de ${activeGameObj?.name} foi encontrada no catálogo para importação.`
      });
      return;
    }

    setIsProcessing(true);
    setProgress({ current: 0, total: data.importedItems.length, cardName: 'Salvando cartas na pasta...' });

    try {
      const targetGameId = activeGameObj?.id || getGameId(targetGameSlug, 1);
      const targetGameName = activeGameObj?.name || getGameName(targetGameSlug);
      let totalAdded = 0;

      if (isSystem) {
        const listType = folderId === 'colecao' ? 'cards' : folderId as 'wishlist' | 'offerlist';
        const payload = data.importedItems.map(item => ({
          ...item.card,
          quantity: item.quantity,
          game_id: item.card.game_id || targetGameId,
          game: item.card.game || targetGameName
        }));

        await addCardsBatchToList(userId, listType, payload, {
          useCardQuantity: true,
          onlyMissing: false
        });

        totalAdded = payload.reduce((acc, curr) => acc + curr.quantity, 0);
      } else {
        // Pasta Personalizada (Fichário): adiciona em binder_cards SEM tocar na coleção
        for (const item of data.importedItems) {
          const cardPayload = {
            ...item.card,
            quantity: item.quantity,
            game_id: item.card.game_id || targetGameId,
            game: item.card.game || targetGameName
          };
          await addCardToBinder(userId, folderId, cardPayload);
          totalAdded += item.quantity;
        }
      }

      invalidateFoldersStatsCache(userId);
      setIsProcessing(false);

      setResultSummary({
        success: true,
        addedCount: totalAdded,
        notFound: data.notFoundItems || []
      });

      onSuccess();
    } catch (err: any) {
      console.error('Erro na importação da lista:', err);
      setIsProcessing(false);
      setResultSummary({
        success: false,
        addedCount: 0,
        notFound: [],
        error: err.message || 'Erro inesperado ao importar cartas.'
      });
    }
  };

  const handleCopyNotFound = () => {
    if (!resultSummary || resultSummary.notFound.length === 0) return;
    const text = resultSummary.notFound.map(n => n.rawLine || `${n.quantity}x ${n.code || n.name}`).join('\n');
    navigator.clipboard.writeText(text);
    setHasCopiedMissing(true);
    setTimeout(() => setHasCopiedMissing(false), 2500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 10 }}
        className="bg-slate-900 border border-slate-800 rounded-3xl p-5 sm:p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto space-y-3.5 shadow-2xl relative my-auto"
      >
        {/* Cabeçalho */}
        <div className="flex items-center justify-between pb-3 border-b border-white/5">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-xl bg-purple-600/15 border border-purple-500/25 text-purple-400 flex items-center justify-center text-sm shrink-0">
              <i className="fas fa-file-import"></i>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="text-base font-black text-white uppercase tracking-tight">
                Importar Cartas
              </h3>
              <span className="text-slate-500 text-xs">·</span>
              <span className="text-xs text-purple-300 font-semibold truncate max-w-[200px]">
                {folder?.name || 'Pasta'}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="w-7 h-7 rounded-lg bg-slate-800/80 hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center text-xs transition-colors cursor-pointer border border-white/5"
            title="Fechar"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Navegação entre Abas: 1. Lista / Entrada vs 2. Prévia das Cartas */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-2">
          <div className="flex items-center space-x-1 bg-slate-950 p-1 rounded-xl border border-slate-800">
            <button
              type="button"
              onClick={() => setActiveTab('input')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-1.5 cursor-pointer ${
                activeTab === 'input'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
              }`}
            >
              <i className="fas fa-list-ol text-[10px]"></i>
              <span>1. Lista / Texto</span>
            </button>

            <button
              type="button"
              onClick={() => {
                if (!validatedData && rawText.trim() && parsedPreview.length > 0) {
                  handleGeneratePreview();
                } else {
                  setActiveTab('preview');
                }
              }}
              disabled={!rawText.trim() || parsedPreview.length === 0}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-1.5 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                activeTab === 'preview'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
              }`}
            >
              <i className="fas fa-eye text-[10px]"></i>
              <span>2. Prévia das Cartas</span>
              {totalCardsCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/30 text-purple-200 font-mono ml-1 font-bold">
                  {totalCardsCount}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'preview' && validatedData && (
            <div className="flex items-center space-x-1.5">
              <button
                type="button"
                onClick={() => setPreviewLayout(prev => prev === 'grid' ? 'list' : 'grid')}
                className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs transition-colors cursor-pointer border border-white/5"
                title={previewLayout === 'grid' ? 'Alternar para lista' : 'Alternar para grade'}
              >
                <i className={`fas ${previewLayout === 'grid' ? 'fa-list' : 'fa-grip'} text-xs`}></i>
              </button>
              <button
                type="button"
                onClick={handleGeneratePreview}
                disabled={isProcessing}
                className="text-[11px] font-semibold text-purple-300 hover:text-white px-2.5 py-1 rounded-lg bg-purple-950/60 border border-purple-500/30 cursor-pointer flex items-center gap-1 active:scale-95 transition-all"
                title="Atualizar prévia com catálogo"
              >
                <i className="fas fa-rotate text-[10px]"></i>
                <span className="hidden sm:inline">Recarregar</span>
              </button>
            </div>
          )}
        </div>

        {/* ABA 1: ENTRADA DE DADOS E CONFIGURAÇÃO */}
        {activeTab === 'input' && (
          <div className="space-y-3 animate-in fade-in duration-150">
            {/* Barra de Configuração Compacta: Fonte, Formatos e Jogo */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 p-2.5 rounded-2xl bg-slate-950/70 border border-slate-800/80">
              {/* Fonte & Formatos Dinâmicos */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Seletor de Fonte (Preparado para múltiplas fontes) */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Fonte:</span>
                  {AVAILABLE_IMPORT_SOURCES.length > 1 ? (
                    <select
                      value={selectedSourceId}
                      onChange={(e) => {
                        setSelectedSourceId(e.target.value);
                        setResultSummary(null);
                        setValidatedData(null);
                      }}
                      disabled={isProcessing}
                      className="bg-slate-900 border border-slate-700 text-white rounded-lg px-2 py-1 text-xs font-semibold outline-none cursor-pointer"
                    >
                      {AVAILABLE_IMPORT_SOURCES.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-purple-950/60 border border-purple-500/30 text-purple-300 font-bold text-xs">
                      <i className={`fas ${currentSource.icon} text-purple-400 text-[10px]`}></i>
                      <span>{currentSource.name}</span>
                    </span>
                  )}
                </div>

                <div className="hidden sm:block h-3.5 w-px bg-slate-800" />

                {/* Formatos Dinâmicos da Fonte Selecionada em Segmented Control */}
                <div className="inline-flex items-center bg-slate-900 p-0.5 rounded-lg border border-slate-800">
                  {availableFormats.map((fmt) => {
                    const isSelected = selectedFormatId === fmt.id;
                    return (
                      <button
                        key={fmt.id}
                        type="button"
                        onClick={() => {
                          setSelectedFormatId(fmt.id);
                          setResultSummary(null);
                          setValidatedData(null);
                        }}
                        disabled={isProcessing}
                        className={`px-2.5 py-1 text-xs font-bold rounded-md transition-all flex items-center gap-1.5 cursor-pointer ${
                          isSelected
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                        }`}
                        title={fmt.description}
                      >
                        <i className={`fas ${fmt.icon} text-[10px]`}></i>
                        <span>{fmt.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Jogo / Franquia */}
              <div className="flex items-center gap-1.5 shrink-0 justify-between sm:justify-end">
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0">Jogo:</span>
                </div>
                {isGameFixed ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 text-xs font-semibold">
                    <i className={`${getGameIcon(activeGameObj?.id || 1)} text-purple-400 text-xs shrink-0`}></i>
                    <span className="truncate max-w-[130px]">{activeGameObj?.name || 'Jogo Fixo'}</span>
                  </span>
                ) : (
                  <div className="relative">
                    <select
                      value={activeGameObj?.slug || selectedGameSlug}
                      onChange={(e) => {
                        setSelectedGameSlug(e.target.value);
                        setValidatedData(null);
                        setResultSummary(null);
                      }}
                      disabled={isProcessing}
                      className="bg-slate-900 border border-slate-700 focus:border-purple-500 rounded-lg px-2.5 py-1 text-xs font-semibold text-white outline-none cursor-pointer truncate max-w-[170px]"
                    >
                      {selectableGames.map(g => (
                        <option key={g.id || g.slug} value={g.slug || String(g.id)}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                    {detectionInfo.detectedGame?.name && (
                      <span 
                        className="absolute -top-1 -right-1 flex h-2 w-2"
                        title={`Detectado automaticamente: ${detectionInfo.detectedGame.name}`}
                      >
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Entrada & Toolbar de Ações Rápidas */}
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="font-bold text-slate-300">Entrada</span>
                  {totalCardsCount > 0 ? (
                    <span className="text-[11px] text-purple-400 font-mono font-bold">
                      ({parsedPreview.length} {parsedPreview.length === 1 ? 'item' : 'itens'} · {totalCardsCount} {totalCardsCount === 1 ? 'cópia' : 'cópias'})
                    </span>
                  ) : (
                    <span className="text-[11px] text-slate-500">
                      (cole o texto ou carregue o arquivo)
                    </span>
                  )}
                </div>

                {/* Toolbar de Ações */}
                <div className="flex items-center gap-1.5">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={currentFormat.fileAccept || '.txt,.csv,.json'}
                    onChange={handleFileUpload}
                    className="hidden"
                  />

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProcessing}
                    className="text-[11px] font-semibold text-slate-300 hover:text-white transition-colors flex items-center gap-1 bg-slate-800 hover:bg-slate-700/80 px-2 py-1 rounded-lg border border-slate-700/60 active:scale-95 cursor-pointer"
                    title={`Carregar arquivo (${currentFormat.fileAccept})`}
                  >
                    <i className="fas fa-file-arrow-up text-cyan-400 text-[10px]"></i>
                    <span>Arquivo</span>
                  </button>

                  <button
                    type="button"
                    onClick={handlePasteClipboard}
                    disabled={isProcessing}
                    className="text-[11px] font-semibold text-slate-300 hover:text-white transition-colors flex items-center gap-1 bg-slate-800 hover:bg-slate-700/80 px-2 py-1 rounded-lg border border-slate-700/60 active:scale-95 cursor-pointer"
                    title="Colar da área de transferência"
                  >
                    <i className="fas fa-paste text-purple-400 text-[10px]"></i>
                    <span>Colar</span>
                  </button>

                  {rawText && (
                    <button
                      type="button"
                      onClick={() => handleTextChange('')}
                      disabled={isProcessing}
                      className="text-[11px] font-semibold text-slate-400 hover:text-red-400 transition-colors px-1.5 py-1 cursor-pointer"
                      title="Limpar texto"
                    >
                      Limpar
                    </button>
                  )}
                </div>
              </div>

              <textarea
                value={rawText}
                onChange={(e) => handleTextChange(e.target.value)}
                disabled={isProcessing}
                rows={5}
                placeholder={currentFormat.placeholder}
                className="w-full bg-slate-950 border border-slate-800 focus:border-purple-500 rounded-xl p-3 text-xs font-mono text-white placeholder:text-slate-600 outline-none transition-all resize-y leading-relaxed"
              />
            </div>

            {/* Aviso informativo de múltiplos jogos no arquivo exportado */}
            {otherGamesCount > 0 && parsedPreview.length > 0 && (
              <div className="p-2.5 rounded-xl bg-purple-950/40 border border-purple-500/30 text-xs flex items-start gap-2 animate-in fade-in duration-150">
                <i className="fas fa-circle-info text-purple-400 text-xs mt-0.5 shrink-0"></i>
                <div className="space-y-1 flex-1">
                  <p className="font-semibold text-purple-200">
                    Arquivo contém cartas de múltiplos jogos ({detectedGamesInFile.map(g => g.name).join(', ')})
                  </p>
                  <p className="text-[11px] text-purple-300/80">
                    Exibindo <span className="font-bold text-white">{parsedPreview.length}</span> cartas de <span className="font-bold text-white">{activeGameObj?.name}</span>. As demais {otherGamesCount} cartas de outros jogos podem ser importadas selecionando o respectivo jogo no seletor acima.
                  </p>
                  {!isGameFixed && detectedGamesInFile.length > 1 && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                      <span className="text-[10px] text-purple-300 font-medium">Trocar jogo para:</span>
                      {detectedGamesInFile
                        .filter(g => !isCardNexusGameMatch(g.slug, targetGameSlug) && !isGame(g.id || g.slug, targetGameSlug))
                        .map(g => (
                          <button
                            key={g.slug || g.id}
                            type="button"
                            onClick={() => {
                              setSelectedGameSlug(g.slug || String(g.id));
                              setValidatedData(null);
                              setResultSummary(null);
                            }}
                            className="px-2 py-0.5 rounded-md bg-purple-600/50 hover:bg-purple-600 border border-purple-400/40 text-purple-100 hover:text-white font-bold text-[10px] transition-colors cursor-pointer"
                          >
                            {g.name}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Alerta se o arquivo tiver cartas mas nenhuma do jogo selecionado */}
            {parsedPreview.length === 0 && allParsedCards.length > 0 && (
              <div className="p-3 rounded-xl bg-amber-950/40 border border-amber-500/30 text-xs flex items-start gap-2.5 animate-in fade-in duration-150">
                <i className="fas fa-triangle-exclamation text-amber-400 text-sm mt-0.5 shrink-0"></i>
                <div className="space-y-1 flex-1">
                  <p className="font-bold text-amber-200">
                    Nenhuma carta de {activeGameObj?.name} encontrada no arquivo
                  </p>
                  <p className="text-[11px] text-amber-300/80">
                    O arquivo carregado possui {allParsedCards.length} cartas de: <span className="font-semibold text-white">{detectedGamesInFile.map(g => g.name).join(', ')}</span>.
                  </p>
                  {!isGameFixed && detectedGamesInFile.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      <span className="text-[10px] text-slate-400">Alternar jogo obrigatório para:</span>
                      {detectedGamesInFile.map(g => (
                        <button
                          key={g.slug || g.id}
                          type="button"
                          onClick={() => {
                            setSelectedGameSlug(g.slug || String(g.id));
                            setValidatedData(null);
                            setResultSummary(null);
                          }}
                          className="px-2 py-0.5 rounded-md bg-purple-600/40 hover:bg-purple-600 border border-purple-500/40 text-purple-200 hover:text-white font-bold text-[10px] transition-colors cursor-pointer"
                        >
                          {g.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {isGameFixed && (
                    <p className="text-[10px] text-amber-400 font-medium pt-0.5">
                      Esta pasta é restrita a cartas de {activeGameObj?.name}. Para importar estas cartas, use uma pasta compatível ou uma pasta do sistema (Coleção, Wishlist ou Offerlist).
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Pré-visualização compacta das cartas reconhecidas */}
            {parsedPreview.length > 0 && (
              <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800/80 space-y-1.5">
                <div className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-1.5 text-slate-300 font-bold">
                    <i className="fas fa-check text-emerald-400 text-[10px]"></i>
                    <span>Reconhecidas: {parsedPreview.length} cartas ({totalCardsCount} cópias)</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleGeneratePreview}
                    disabled={isProcessing}
                    className="text-[11px] font-bold text-purple-400 hover:text-purple-300 flex items-center gap-1 cursor-pointer"
                  >
                    <span>Ver Prévia das Cartas</span>
                    <i className="fas fa-arrow-right text-[9px]"></i>
                  </button>
                </div>

                <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto pr-1">
                  {(showAllPreview ? parsedPreview : parsedPreview.slice(0, 10)).map((card, idx) => (
                    <div 
                      key={idx}
                      className="bg-slate-900 border border-slate-800 px-2 py-0.5 rounded-md text-[10px] flex items-center gap-1.5 text-slate-200"
                    >
                      <span className="font-bold text-purple-400">{card.quantity}x</span>
                      <span className="font-medium truncate max-w-[120px]">{card.name}</span>
                      {(card.code || card.setCode || card.cardNum) && (
                        <span className="text-[9px] text-slate-500 font-mono">
                          ({card.setCode ? `${card.setCode} ` : ''}#{card.cardNum || card.code})
                        </span>
                      )}
                      {card.finish && (
                        <span className="text-[9px] text-amber-400 font-bold">
                          ✨ {card.finish}
                        </span>
                      )}
                      {card.price && (
                        <span className="text-[9px] text-emerald-400 font-mono">
                          ${Number(card.price).toFixed(2)}
                        </span>
                      )}
                    </div>
                  ))}
                  {!showAllPreview && parsedPreview.length > 10 && (
                    <button
                      type="button"
                      onClick={() => setShowAllPreview(true)}
                      className="text-[10px] text-purple-400 hover:text-purple-300 font-bold px-2 py-0.5 rounded-md bg-slate-900/60 border border-slate-800 cursor-pointer"
                    >
                      + {parsedPreview.length - 10} restantes...
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ABA 2: PRÉVIA VISUAL DAS CARTAS A IMPORTAR */}
        {activeTab === 'preview' && (
          <div className="space-y-3 animate-in fade-in duration-150">
            {validatedData ? (
              <div className="space-y-3">
                {/* Barra de Resumo da Prévia */}
                <div className="flex items-center justify-between p-2.5 rounded-2xl bg-slate-950/80 border border-slate-800/80 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 font-bold text-[11px]">
                      <i className="fas fa-check-circle text-emerald-400 text-xs"></i>
                      <span>{validatedData.importedItems.length} encontradas no catálogo</span>
                    </span>

                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-950/50 border border-purple-500/25 text-purple-300 font-bold text-[11px] font-mono">
                      <span>{validatedData.totalCards} cópias no total</span>
                    </span>

                    {validatedData.notFoundItems.length > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300 font-bold text-[11px]">
                        <i className="fas fa-triangle-exclamation text-amber-400 text-xs"></i>
                        <span>{validatedData.notFoundItems.length} não encontradas</span>
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveTab('input')}
                    className="text-xs font-semibold text-slate-400 hover:text-purple-300 transition-colors flex items-center gap-1 cursor-pointer shrink-0"
                  >
                    <i className="fas fa-pen text-[10px]"></i>
                    <span className="hidden sm:inline">Editar Lista</span>
                  </button>
                </div>

                {/* Exibição em Grade (Cards com Imagem) */}
                {previewLayout === 'grid' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 max-h-[46vh] overflow-y-auto p-1 pr-1.5 custom-scrollbar">
                    {validatedData.importedItems.map((item, idx) => {
                      const img = parseCardImage(item.card) || item.card.image_url;
                      const isFoil = (item.card as any).finish || item.card.rarity?.toLowerCase().includes('foil');

                      return (
                        <div
                          key={idx}
                          className="bg-slate-950/80 rounded-2xl border border-slate-800 hover:border-purple-500/40 transition-all p-2 flex flex-col justify-between group relative overflow-hidden shadow-md"
                        >
                          <div className="relative aspect-[5/7] rounded-xl overflow-hidden bg-slate-900 border border-white/5 mb-1.5 flex items-center justify-center">
                            <CardImage
                              src={img}
                              alt={item.card.name}
                              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                              game={item.card.game}
                              gameId={item.card.game_id}
                            />
                            {/* Badge de Quantidade */}
                            <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md bg-purple-600/90 backdrop-blur-sm text-white font-mono font-black text-[10px] shadow border border-purple-400/40">
                              {item.quantity}x
                            </span>

                            {/* Badge de Finish/Foil */}
                            {isFoil && (
                              <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded-md bg-amber-500/90 text-slate-950 font-black text-[8px] shadow uppercase tracking-wider flex items-center gap-0.5">
                                <i className="fas fa-sparkles text-[7px]"></i>
                                <span>{(item.card as any).finish || 'Foil'}</span>
                              </span>
                            )}
                          </div>

                          <div className="space-y-0.5">
                            <h4 className="text-xs font-bold text-white truncate" title={item.card.name}>
                              {item.card.name}
                            </h4>
                            <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
                              <span className="truncate max-w-[100px]" title={item.card.set_name || item.card.set_code}>
                                {item.card.set_name || item.card.set_code || 'Oficial'}
                              </span>
                              {item.card.code && (
                                <span className="text-slate-500">#{item.card.code}</span>
                              )}
                            </div>
                            {item.card.price ? (
                              <span className="text-emerald-400 font-mono font-bold text-[10px] block">
                                R$ {Number(item.card.price).toFixed(2)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Exibição em Lista Compacta */}
                {previewLayout === 'list' && (
                  <div className="max-h-[46vh] overflow-y-auto space-y-1.5 pr-1.5 custom-scrollbar">
                    {validatedData.importedItems.map((item, idx) => {
                      const img = parseCardImage(item.card) || item.card.image_url;
                      const isFoil = (item.card as any).finish || item.card.rarity?.toLowerCase().includes('foil');

                      return (
                        <div
                          key={idx}
                          className="bg-slate-950/70 p-2 rounded-xl border border-slate-800/80 flex items-center justify-between gap-3"
                        >
                          <div className="flex items-center space-x-2.5 min-w-0">
                            <div className="w-9 h-12 rounded-lg overflow-hidden bg-slate-900 shrink-0 border border-white/5 relative flex items-center justify-center">
                              <CardImage
                                src={img}
                                alt={item.card.name}
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-xs font-bold text-white truncate">
                                {item.card.name}
                              </h4>
                              <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono">
                                <span>{item.card.set_name || item.card.set_code || 'Coleção'}</span>
                                {item.card.code && <span>#{item.card.code}</span>}
                                {isFoil && (
                                  <span className="text-amber-400 font-bold">✨ {(item.card as any).finish || 'Foil'}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3 shrink-0">
                            {item.card.price ? (
                              <span className="text-xs font-mono font-bold text-emerald-400">
                                R$ {Number(item.card.price).toFixed(2)}
                              </span>
                            ) : null}
                            <span className="px-2 py-0.5 rounded-lg bg-purple-600/30 border border-purple-500/40 text-purple-300 font-mono font-black text-xs">
                              {item.quantity}x
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Se houver cartas não encontradas na prévia */}
                {validatedData.notFoundItems.length > 0 && (
                  <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/25 text-xs space-y-1.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="font-bold text-amber-300 flex items-center gap-1.5">
                        <i className="fas fa-triangle-exclamation text-amber-400"></i>
                        <span>{validatedData.notFoundItems.length} {validatedData.notFoundItems.length === 1 ? 'carta não foi encontrada' : 'cartas não foram encontradas'} no catálogo:</span>
                      </span>
                      <button
                        type="button"
                        onClick={handleCopyNotFound}
                        className="text-purple-300 hover:text-white font-bold underline cursor-pointer text-[10px]"
                      >
                        {hasCopiedMissing ? 'Copiado!' : 'Copiar faltantes'}
                      </button>
                    </div>
                    <div className="max-h-20 overflow-y-auto font-mono text-[10px] text-slate-300 bg-black/40 p-2 rounded-xl space-y-0.5 custom-scrollbar">
                      {validatedData.notFoundItems.map((item, idx) => (
                        <div key={idx} className="truncate">
                          • {item.quantity}x {item.code || item.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 rounded-2xl bg-slate-950/60 border border-slate-800/80 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-purple-600/20 border border-purple-500/30 text-purple-400 flex items-center justify-center mx-auto text-xl">
                  <i className="fas fa-eye"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white">Carregar Prévia das Cartas</h4>
                  <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                    Consulte as {parsedPreview.length} cartas ({totalCardsCount} cópias) diretamente no catálogo oficial para conferir as ilustrações e edições antes de salvar.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleGeneratePreview}
                  disabled={isProcessing || parsedPreview.length === 0}
                  className="bg-purple-600 hover:bg-purple-500 text-white font-bold px-4 py-2 rounded-xl text-xs inline-flex items-center space-x-2 transition-all shadow-lg shadow-purple-600/25 active:scale-95 cursor-pointer"
                >
                  <i className="fas fa-sparkles text-xs"></i>
                  <span>Consultar Catálogo e Ver Prévia</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Barra de Progresso durante a busca */}
        {isProcessing && (
          <div className="bg-slate-950 border border-purple-500/30 rounded-2xl p-3 space-y-2 animate-in fade-in duration-200">
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold text-purple-300 flex items-center gap-2">
                <i className="fas fa-spinner fa-spin text-purple-400"></i>
                <span>Buscando cartas no catálogo oficial...</span>
              </span>
              <span className="font-mono text-slate-400 text-[11px] font-bold">
                {progress.current} / {progress.total}
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
              ></div>
            </div>
            {progress.cardName && (
              <p className="text-[11px] font-mono text-slate-400 truncate">
                Item atual: <span className="text-white">{progress.cardName}</span>
              </p>
            )}
          </div>
        )}

        {/* Resumo do Resultado */}
        {resultSummary && (
          <div className={`p-3.5 rounded-2xl border text-xs space-y-2 ${
            resultSummary.success 
              ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-200' 
              : 'bg-red-950/40 border-red-500/40 text-red-200'
          }`}>
            <div className="flex items-center gap-2 font-bold text-sm">
              <i className={`fas ${resultSummary.success ? 'fa-circle-check text-emerald-400' : 'fa-circle-exclamation text-red-400'}`}></i>
              <span>
                {resultSummary.success 
                  ? `${resultSummary.addedCount} ${resultSummary.addedCount === 1 ? 'carta adicionada' : 'cartas adicionadas'} à pasta com sucesso!`
                  : 'Falha na importação'}
              </span>
            </div>

            {resultSummary.error && (
              <p className="text-[11px] text-red-300">{resultSummary.error}</p>
            )}

            {resultSummary.notFound.length > 0 && (
              <div className="pt-2 border-t border-white/10 space-y-1.5">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-bold text-amber-300">
                    {resultSummary.notFound.length} {resultSummary.notFound.length === 1 ? 'carta não encontrada' : 'cartas não encontradas'} no catálogo:
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyNotFound}
                    className="text-purple-300 hover:text-white font-bold underline cursor-pointer text-[10px]"
                  >
                    {hasCopiedMissing ? 'Copiado!' : 'Copiar lista de faltantes'}
                  </button>
                </div>
                <div className="max-h-20 overflow-y-auto font-mono text-[10px] text-slate-300 bg-black/40 p-2 rounded-xl space-y-0.5 custom-scrollbar">
                  {resultSummary.notFound.map((item, idx) => (
                    <div key={idx} className="truncate">
                      • {item.quantity}x {item.code || item.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Rodapé / Botões de Ação */}
        <div className="flex items-center justify-between gap-2 pt-1 border-t border-white/5">
          <div>
            {activeTab === 'input' && rawText.trim() && parsedPreview.length > 0 && isGameValid && !resultSummary?.success && (
              <button
                type="button"
                onClick={() => handleConfirmImport(null)}
                disabled={isProcessing || !isGameValid}
                className="text-xs font-semibold text-slate-400 hover:text-slate-200 transition-colors cursor-pointer underline underline-offset-4"
              >
                Importar sem ver prévia
              </button>
            )}
            {activeTab === 'preview' && (
              <button
                type="button"
                onClick={() => setActiveTab('input')}
                disabled={isProcessing}
                className="px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <i className="fas fa-arrow-left text-[10px]"></i>
                <span>Editar Lista</span>
              </button>
            )}
          </div>

          <div className="flex items-center space-x-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition-colors cursor-pointer"
            >
              {resultSummary?.success ? 'Concluir' : 'Cancelar'}
            </button>

            {!resultSummary?.success && activeTab === 'input' && (
              <button
                type="button"
                onClick={handleGeneratePreview}
                disabled={isProcessing || !rawText.trim() || parsedPreview.length === 0 || !isGameValid}
                className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-4 sm:px-5 py-2 rounded-xl text-xs flex items-center space-x-2 transition-all shadow-lg shadow-purple-600/20 active:scale-95 cursor-pointer"
              >
                {isProcessing ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Consultando...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-eye text-xs"></i>
                    <span>Ver Prévia {totalCardsCount > 0 ? `(${totalCardsCount})` : ''}</span>
                  </>
                )}
              </button>
            )}

            {!resultSummary?.success && activeTab === 'preview' && (
              <button
                type="button"
                onClick={() => handleConfirmImport(validatedData)}
                disabled={isProcessing || !validatedData || validatedData.importedItems.length === 0 || !isGameValid}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-4 sm:px-5 py-2 rounded-xl text-xs flex items-center space-x-2 transition-all shadow-lg shadow-emerald-600/20 active:scale-95 cursor-pointer"
              >
                {isProcessing ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-check text-xs"></i>
                    <span>Confirmar Importação ({validatedData ? validatedData.totalCards : totalCardsCount})</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};
